/**
 * Topology — Network-wide > Topology (LLDP-derived L2 graph).
 *
 * Renders /api/v2/topology {nodes, edges} as a tidy tree (lib/topologyLayout, OP#1857): every
 * device sits UNDER the node its LLDP uplink points at, and links are elbows that run in the gap
 * between rows, so a link never passes behind an unrelated switch. Read-only SVG (no graph lib).
 *
 * What the drawing says, and why (each was misleading before OP#1857):
 *   - Unmanaged upstream switch: dashed outline + HOLLOW neutral dot, "unmanaged". We don't
 *     manage it, so we don't know whether it is up -- it was drawn green.
 *   - Stale link (reporter offline, or its LLDP not re-confirmed): DASHED, with its age
 *     ("last seen 11d ago"). A solid line is a current LLDP neighbour ("as of 2m ago").
 *   - Managed device with no LLDP neighbour: on the "Uplink unknown" shelf below the tree, with
 *     a dashed stub -- instead of silently having no link.
 *
 * Design system: neutral surfaces + ink for structure; the status ramp (healthy / warn /
 * critical) ONLY on a managed node's state dot. Theme-aware via tokens + currentColor (light
 * and dark), no hard-coded colours.
 */
import { useTopology, type TopoEdge, type TopoNode } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { DEFAULT_DIMS, elbowPath, layoutTopology } from "@/lib/topologyLayout";
import { lldpAge } from "@/lib/lldpAge";

const D = DEFAULT_DIMS;
const { nodeW: NODE_W, nodeH: NODE_H } = D;

/** Status-ramp class for a MANAGED node's dot; unmanaged nodes never get one. */
function dotClass(n: TopoNode): string {
  if (n.health === "critical") return "fill-critical";
  if (n.health === "warn") return "fill-warn";
  if (n.online) return "fill-healthy";
  return "fill-ink-subtle"; // dormant / unknown: neutral, not a state colour
}

/** Second line of a node box: kind + address, or the reason it is special. */
function subLabel(n: TopoNode): string {
  if (!n.managed) return `unmanaged${n.ip ? ` · ${n.ip}` : ""}`;
  // Kept short: the box is 150px and the shelf caption already explains the reason.
  if (n.uplink === "unknown") return "uplink unknown";
  return `${n.kind}${n.ip ? ` · ${n.ip}` : ""}`;
}

export default function Topology() {
  const { data, isLoading, error } = useTopology();
  const nodes = data?.nodes ?? [];
  const edges = data?.edges ?? [];
  const L = layoutTopology(nodes, edges);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const now = Date.now();

  const stale = data?.counts.stale ?? edges.filter((e) => e.stale).length;
  const unknown = data?.counts.uplink_unknown ?? L.shelf.length;
  // Links resolved to another site of the org (no same-site candidate; OP#1919).
  const crossSite = data?.counts.cross_site ?? edges.filter((e) => e.cross_site).length;
  const subtitle = data
    ? [
        `${data.counts.managed} managed`,
        `${data.counts.external} unmanaged upstream`,
        `${data.counts.edges} links (LLDP)`,
        stale ? `${stale} stale` : null,
        unknown ? `${unknown} uplink unknown` : null,
        crossSite ? `${crossSite} cross-site` : null,
      ].filter(Boolean).join(" · ")
    : "LLDP-derived Layer-2 map";

  return (
    <div>
      <PageHeader title="Topology" subtitle={subtitle} />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Building topology…</div>}

      {data && nodes.length === 0 && (
        <EmptyState
          title="No topology yet"
          hint="Topology is derived from LLDP. Once devices report an uplink neighbor (APs via owgw, switches via the LLDP scan), the map appears here."
        />
      )}

      {nodes.length > 0 && (
        <div className="md-card overflow-x-auto p-4">
          <svg viewBox={`0 0 ${L.width} ${L.height}`} width={L.width} height={L.height} role="img"
            aria-label="Network topology (LLDP)" className="max-w-none text-ink" data-testid="topology-svg">
            {/* Links first so boxes paint over the line ends. */}
            {edges.map((e, i) => (
              <TopoLink key={`${e.source}->${e.target}-${i}`} edge={e} L={L}
                sourceOnline={byId.get(e.source)?.online ?? null} now={now} />
            ))}

            {/* "Uplink unknown" shelf caption + a dashed stub above each shelved device. */}
            {L.shelfCaptionY !== null && (
              <text x={D.pad} y={L.shelfCaptionY} className="fill-ink-muted" fontSize={11} data-testid="topo-shelf-caption">
                Uplink unknown: no LLDP neighbour reported
              </text>
            )}
            {L.shelf.map((id) => {
              const p = L.pos.get(id)!;
              return (
                <line key={`stub-${id}`} x1={p.x + NODE_W / 2} y1={p.y} x2={p.x + NODE_W / 2} y2={p.y - 8}
                  stroke="currentColor" strokeOpacity={0.35} strokeWidth={1.5} strokeDasharray="2 3" />
              );
            })}

            {/* Nodes. */}
            {nodes.map((n) => {
              const p = L.pos.get(n.id);
              if (!p) return null;
              return (
                <g key={n.id} data-testid="topo-node" data-node-id={n.id} data-managed={n.managed ? "true" : "false"}
                  data-x={p.x} data-y={p.y}>
                  <title>{`${n.label}${n.managed ? "" : " (unmanaged: state unknown)"}${n.state && n.managed ? ` · ${n.state}` : ""}`}</title>
                  <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={8}
                    className={n.managed ? "fill-surface-2" : "fill-surface"}
                    stroke="currentColor" strokeOpacity={n.managed ? 0.25 : 0.4}
                    strokeDasharray={n.managed ? undefined : "4 3"} />
                  {n.managed ? (
                    <circle cx={p.x + 14} cy={p.y + NODE_H / 2} r={4} className={dotClass(n)} data-testid="topo-dot" />
                  ) : (
                    // Hollow, neutral: "we don't know" -- never a status colour.
                    <circle cx={p.x + 14} cy={p.y + NODE_H / 2} r={3.5} fill="none" stroke="currentColor"
                      strokeOpacity={0.55} strokeWidth={1.5} data-testid="topo-dot-unknown" />
                  )}
                  <text x={p.x + 26} y={p.y + 18} className="fill-ink" fontSize={12} fontWeight={500}>
                    {n.label.length > 16 ? n.label.slice(0, 15) + "…" : n.label}
                  </text>
                  <text x={p.x + 26} y={p.y + 33} className="fill-ink-subtle" fontSize={10}>
                    {subLabel(n)}
                  </text>
                </g>
              );
            })}
          </svg>

          <Legend />
        </div>
      )}
    </div>
  );
}

/** One LLDP link: an elbow from the device up to its neighbour, labelled port/VLAN + age. */
function TopoLink({ edge, L, sourceOnline, now }: {
  edge: TopoEdge;
  L: ReturnType<typeof layoutTopology>;
  sourceOnline: boolean | null;
  now: number;
}) {
  const a = L.pos.get(edge.source);
  const b = L.pos.get(edge.target);
  if (!a || !b) return null;
  const age = lldpAge(edge, sourceOnline, now);
  const stale = edge.stale === true || age.stale;
  // A neighbour matched at ANOTHER site (the reporter's site had no candidate) is said so on
  // the link, so a cross-building drawing is never mistaken for a same-site cable (OP#1919).
  const crossSite = edge.cross_site === true;
  const port = [edge.port, edge.vlan != null ? `vlan ${edge.vlan}` : null, crossSite ? "other site" : null]
    .filter(Boolean).join(" · ");
  // Labels sit just above the child, on its leg, with a surface-coloured halo over the line.
  const lx = a.x + NODE_W / 2;
  const ly = a.y - 8;
  return (
    <g data-testid="topo-edge" data-source={edge.source} data-target={edge.target} data-stale={stale ? "true" : "false"}
      data-cross-site={crossSite ? "true" : "false"}>
      {crossSite && <title>Matched to a device at another site: this site has no device by that name/MAC</title>}
      <path d={elbowPath(a, b)} fill="none" stroke="currentColor"
        strokeOpacity={stale ? 0.4 : 0.32} strokeWidth={1.5}
        strokeDasharray={stale ? "5 4" : undefined} data-testid="topo-edge-line" />
      {port && (
        <text x={lx} y={ly - 12} textAnchor="middle" fontSize={10} className="fill-ink-subtle stroke-surface"
          strokeWidth={3} paintOrder="stroke">
          {port}
        </text>
      )}
      <text x={lx} y={ly} textAnchor="middle" fontSize={10} strokeWidth={3} paintOrder="stroke"
        className={stale ? "fill-ink-muted stroke-surface italic" : "fill-ink-subtle stroke-surface"}
        data-testid="topo-edge-age">
        {age.label}
      </text>
    </g>
  );
}

/** Key for the line and dot styles (tokens only; reads in light and dark). */
function Legend() {
  const item = "inline-flex items-center gap-2";
  return (
    <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-muted" aria-label="Legend" data-testid="topo-legend">
      <li className={item}>
        <svg width="28" height="8" aria-hidden className="text-ink"><line x1="0" y1="4" x2="28" y2="4" stroke="currentColor" strokeOpacity={0.5} strokeWidth={1.5} /></svg>
        Current LLDP link (as of)
      </li>
      <li className={item}>
        <svg width="28" height="8" aria-hidden className="text-ink"><line x1="0" y1="4" x2="28" y2="4" stroke="currentColor" strokeOpacity={0.5} strokeWidth={1.5} strokeDasharray="5 4" /></svg>
        Stale: last known, not current
      </li>
      <li className={item}>
        <span aria-hidden className="text-[10px] text-ink-subtle">other site</span>
        Neighbour matched at another site (none at this site)
      </li>
      <li className={item}>
        <svg width="10" height="10" aria-hidden className="text-ink"><circle cx="5" cy="5" r="3.5" fill="none" stroke="currentColor" strokeOpacity={0.55} strokeWidth={1.5} /></svg>
        Unmanaged: state unknown
      </li>
      <li className={item}>
        <svg width="10" height="10" aria-hidden><circle cx="5" cy="5" r="4" className="fill-healthy" /></svg>
        Managed: online / warn / critical
      </li>
    </ul>
  );
}
